import { DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { DATE_RANGE_PRESETS, resolvePresetRange, toEndOfDayString, toStartOfDayString } from './dateRangePresets';

const { RangePicker } = DatePicker;

// Value shape: { preset, from, to } — resolved at selection time so
// buildFilterConditions never redoes date math. Both ends are full ISO
// datetimes covering the user's local calendar day (start-of-day / end-of-day);
// a bare YYYY-MM-DD would be read server-side as midnight UTC, which clips
// either end for anyone not on UTC.
export default function DateRangePresetField({ field, value, onChange, disabled }) {
  const current = value && typeof value === 'object' ? value : {};
  const preset = current.preset;

  const handlePresetChange = (nextPreset) => {
    if (nextPreset === 'custom') {
      // Nothing resolved yet — wait for the RangePicker below.
      onChange({ preset: nextPreset, from: null, to: null });
      return;
    }
    const [from, to] = resolvePresetRange(nextPreset);
    onChange({ preset: nextPreset, from: toStartOfDayString(from), to: toEndOfDayString(to) });
  };

  const handleCustomRangeChange = (dates) => {
    const [from, to] = Array.isArray(dates) ? dates : [null, null];
    onChange({ preset: 'custom', from: toStartOfDayString(from), to: toEndOfDayString(to) });
  };

  const customRangeValue = preset === 'custom' && (current.from || current.to)
    ? [current.from ? dayjs(current.from) : null, current.to ? dayjs(current.to) : null]
    : null;

  return (
    <div className="sbv1-date-range-preset">
      <Select
        value={preset}
        disabled={disabled}
        allowClear
        placeholder={field.placeholder ?? 'Select date'}
        options={DATE_RANGE_PRESETS.map(({ key, label }) => ({ value: key, label }))}
        onChange={handlePresetChange}
        onClear={() => onChange(undefined)}
      />
      {preset === 'custom' && (
        // A plain RangePicker — clicking the Start Date segment opens the
        // calendar by antd's own default behaviour, nothing custom needed.
        <RangePicker
          style={{ width: '100%', marginTop: 8 }}
          disabled={disabled}
          value={customRangeValue}
          disabledDate={(current_) => current_ && current_.isAfter(dayjs(), 'day')}
          onChange={handleCustomRangeChange}
        />
      )}
    </div>
  );
}

DateRangePresetField.propTypes = {
  field: PropTypes.object.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
